import React from 'react';
import { Card, CardContent, CardActions, Button } from '@mui/material';
import parse from 'html-react-parser';
import NoteAddRoundedIcon from '@mui/icons-material/NoteAddRounded';
import BackspaceRoundedIcon from '@mui/icons-material/BackspaceRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import SentimentGauge from './sentimentGauge';
import HandshakeIcon from '@mui/icons-material/Handshake';
import './HtmlRenderer.css';
import showdown from 'showdown';

function HtmlRenderer({ html, onClear, show, onAddNote, selectedMenuItem, onAddType, onAddPriority, onAddBoard, onAddTeam, onAddAgreement, onAddTitle, showSentiment, sentimentRating, onHandleOpenWindow, onUpdateTicket }) {
    if (!show || !html) {
        return null;
    }

    const excludeTimeEntry = ['search_university', 'ticket_sentiment', 'translate_text','similar_tickets_history'];

    const converter = new showdown.Converter({ simpleLineBreaks: true });
    const convertedHtml = converter.makeHtml(html);
    const cleanedHtml = convertedHtml
        .replace(/(\r\n|\n|\r)+/g, ' ') // Replace multiple line breaks with a single space
        .replace(/>\s+</g, '><')       // Remove spaces between HTML tags
        .trim();                       // Remove leading and trailing whitespace
    const parsedHtml = parse(cleanedHtml);

    const handleClear = () => {
        onClear();
    };

    const handleAddType = () => {
        onAddType(html);
    };

    const handleAddPriority = () => {
        onAddPriority(html);
    };

    const handleAddBoard = () => {
        onAddBoard(html);
    };

    const handleAddTeam = () => {
        onAddTeam(html);
    };

    const handleAddAgreement = () => {
        onAddAgreement(html);
    };

    const handleAddTitle = () => {
        onAddTitle(html);
    };

    const handleUpdateTicket = () => {
        onUpdateTicket(html)
    };

    const handleCopy = (textToCopy) => {
        let text;
        try {
            text = typeof textToCopy === 'string' ? textToCopy : JSON.stringify(textToCopy);
        } catch (err) {
            console.error("Sidekick - Failed to stringify textToCopy: ", err);
            //alert("Failed to copy text: Circular reference detected.");
            return;
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.permissions.query({ name: "clipboard-write" }).then(result => {
                if (result.state === "granted" || result.state === "prompt") {
                    navigator.clipboard.writeText(text)
                        .then(() => console.log("Sidekick - Text copied to clipboard"))
                        .catch(err => {
                            console.error("Sidekick - Failed to copy text: ", err);
                            //alert("Sidekick - Clipboard API access is blocked. Trying to use a fallback method.");
                            fallbackCopyTextToClipboard(text);
                        });
                } else {
                    //alert("Sidekick - Clipboard API access is blocked. Trying to use a fallback method.");
                    fallbackCopyTextToClipboard(text);
                }
            });
        } else {
            fallbackCopyTextToClipboard(text);
        }
    };

    function fallbackCopyTextToClipboard(textToCopy) {
        let textArea = document.createElement("textarea");
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        try {
            let successful = document.execCommand('copy');
            console.log('Sidekick - Copy Fallback: Copying text command was ' + (successful ? 'successful' : 'unsuccessful'));
        } catch (err) {
            console.error('Sidekick - Copy Fallback: Oops, unable to copy', err);
        }
        document.body.removeChild(textArea);
    };

    const handleNote = (resolution) => {
        onAddNote(html, resolution);
    };

    const handleOpenWindow = () => {
        onHandleOpenWindow(html);
    };

return (
    <Card
        sx={
            html
                ? {
                    minHeight: "400px",
                    height: "490px",
                    display: "flex",
                    flexDirection: "column",
                    whiteSpace: "pre-line",
                    padding: 0,
                    boxShadow: "none",
                    borderRadius: 0,
                    transition: "none",
                }
                : {}
        }
    >
        <CardContent sx={html ? { padding: '10px', overflowY: 'auto', flex: 1, border: '1px solid darkgray', borderRadius: '12px' } : {}}>
            {parsedHtml}
            {showSentiment !== null && showSentiment !== undefined && showSentiment !== "" && (<SentimentGauge sentiment={showSentiment} value={sentimentRating} />)}
        </CardContent>
        <CardActions sx={{ mt: "auto", display: 'flex', padding: '8px 8px 8px 0px' }}>
            <Button variant="contained" color="primary" size="small" onClick={handleClear} startIcon={<BackspaceRoundedIcon />}>Back</Button>
            {selectedMenuItem === "categorize_ticket" ? (
                <Button variant="contained" color="primary" size="small" onClick={handleAddType} startIcon={<EditNoteIcon />}>Update Ticket Categorization</Button>
            ) : selectedMenuItem === "recommend_priority" ? (
                <Button variant="contained" color="primary" size="small" onClick={handleAddPriority} startIcon={<PriorityHighIcon />}>Update Priority</Button>
            ) : selectedMenuItem === "recommend_board" ? (
                <Button variant="contained" color="primary" size="small" onClick={handleAddBoard} startIcon={<DashboardCustomizeIcon />}>Update Board</Button>
            ) : selectedMenuItem === "recommend_team" ? (
                <Button variant="contained" color="primary" size="small" onClick={handleAddTeam} startIcon={<GroupAddIcon />}>Update Team</Button>
            ) : selectedMenuItem === "recommend_agreement" ? (
                <Button variant="contained" color="primary" size="small" onClick={handleAddAgreement} startIcon={<HandshakeIcon />}>Update Agreement</Button>
            ) : selectedMenuItem === "recommend_title" ? (
                <Button variant="contained" color="primary" size="small" onClick={handleAddTitle} startIcon={<EditNoteIcon />}>Update Title</Button>
            ) : (
                <>
                    <Button variant="contained" color="primary" size="small" onClick={() => handleCopy(html)} startIcon={<ContentCopyRoundedIcon />}>Copy</Button>
                    <Button variant="contained" color="primary" size="small" onClick={() => handleNote(false)} startIcon={<NoteAddRoundedIcon />} >
                        <span className="short-text">Add as Note</span>
                        <span className="short-text-small">Add Note</span>
                    </Button>
                    {selectedMenuItem === "recommend_solution" && (
                        <Button variant="contained" color="primary" size="small" onClick={() => handleNote(true)} startIcon={<NoteAddRoundedIcon />} >
                            <span className="short-text">Add as Resolution</span>
                            <span className="short-text-small">Add Resolution</span>
                        </Button>
                    )}
                    {selectedMenuItem === "ticket_sentiment" && (
                        <Button variant="contained" color="primary" size="small" onClick={handleUpdateTicket} startIcon={<EditNoteIcon />} >Update Ticket</Button>
                    )}
                    {/*(!excludeTimeEntry.includes(selectedMenuItem)) && (
                        <Button variant="contained" color="primary" size="small" onClick={handleOpenWindow} startIcon={<NoteAddRoundedIcon />} >Add Time Entry</Button>
                    )*/}
                </>
            )}
        </CardActions>
    </Card>
);
}
export default HtmlRenderer;